import { useState, useEffect, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TextInput, PasswordInput, Button, Box, Text, Alert, Stack, Modal, ActionIcon, PinInput, Group } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertCircle, IconSettings } from '@tabler/icons-react'
import { Capacitor } from '@capacitor/core'
import { useAuth } from '../hooks/useAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getTenantFromSubdomain } from '../utils/tenant'
import { getPublicCompanyInfo } from '../api/client'
import { API_URL_OVERRIDE_KEY, TENANT_HOST_OVERRIDE_KEY } from '../api/client'

// PIN สำหรับเข้าตั้งค่า backend URL ตอน runtime บนแอปมือถือ — hardcode ไว้ในแอปเอง
// (ไม่ใช่ระบบความปลอดภัยระดับสูง แค่กันคนทั่วไปเผลอกดยุ่ง ดูรายละเอียดเพิ่มเติมในแชท)
const BACKEND_SETTINGS_PIN = '301233'

interface TenantInfo {
  name: string
  logoUrl: string | null
  themeColor: string | null
  code: string
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { doLogin, token } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [didLogin, setDidLogin] = useState(false)
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null)

  // ── PIN-protected Backend URL settings (native app เท่านั้น) ──────────────
  const [settingsOpen, { open: openSettings, close: closeSettings }] = useDisclosure(false)
  const [pinValue, setPinValue] = useState('')
  const [pinError, setPinError] = useState(false)
  const [unlocked, setUnlocked] = useState(false)
  const [backendUrlInput, setBackendUrlInput] = useState('')
  const [tenantHostInput, setTenantHostInput] = useState('')

  const handlePinComplete = (val: string) => {
    if (val === BACKEND_SETTINGS_PIN) {
      setUnlocked(true)
      setPinError(false)
      setBackendUrlInput(localStorage.getItem(API_URL_OVERRIDE_KEY) ?? import.meta.env.VITE_API_URL ?? '')
      setTenantHostInput(localStorage.getItem(TENANT_HOST_OVERRIDE_KEY) ?? '')
    } else {
      setPinError(true)
      setPinValue('')
    }
  }

  const closeSettingsModal = () => {
    closeSettings()
    setPinValue('')
    setPinError(false)
    setUnlocked(false)
  }

  const handleSaveBackendUrl = () => {
    const url = backendUrlInput.trim().replace(/\/+$/, '')
    if (url) localStorage.setItem(API_URL_OVERRIDE_KEY, url)
    else localStorage.removeItem(API_URL_OVERRIDE_KEY)

    const tenantHost = tenantHostInput.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '')
    if (tenantHost) localStorage.setItem(TENANT_HOST_OVERRIDE_KEY, tenantHost)
    else localStorage.removeItem(TENANT_HOST_OVERRIDE_KEY)

    // ต้อง reload เพราะ axios baseURL ถูกตั้งครั้งเดียวตอน module load
    window.location.reload()
  }

  const tenantCode = getTenantFromSubdomain()

  // โหลดข้อมูล tenant (โลโก้, ชื่อ) จาก subdomain
  useEffect(() => {
    if (!tenantCode) return
    getPublicCompanyInfo(tenantCode)
      .then(({ data }) => { if (data?.name) setTenantInfo(data) })
      .catch(() => {})
  }, [tenantCode])

  useEffect(() => {
    if (token && didLogin) {
      const redirect = searchParams.get('redirect')
      navigate(redirect && redirect.startsWith('/') ? redirect : '/', { replace: true })
    }
  }, [token, didLogin])

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault()
    if (!username || !password) return
    setLoading(true); setError('')
    try {
      await doLogin(username, password)
      setDidLogin(true)
    } catch {
      setError('Invalid username or password')
    } finally { setLoading(false) }
  }

  // ── ค่า default นี้ใช้เฉพาะตอนยังไม่รู้ tenant (เช่น login page ก่อน resolve) หรือ tenant
  // ไม่เคยตั้ง themeColor เอง — เปลี่ยนจาก teal เป็นน้ำเงินแบรนด์ใหม่ตามโลโก้ AssetOS ไม่กระทบ
  // tenant ที่ตั้งสีของตัวเองไว้แล้วเลยแม้แต่นิดเดียว (ยังคง fallback เท่านั้น) ──
  const accentColor = tenantInfo?.themeColor ?? '#2563eb'
  const accentLight = accentColor + '22'

  return (
    <Box style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Sans, sans-serif' }}>
      <Box style={{ position: 'fixed', inset: 0, backgroundImage: 'radial-gradient(rgba(110,231,183,0.03) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

      {Capacitor.isNativePlatform() && (
        <ActionIcon
          variant="subtle" color="gray" size="lg" radius="xl"
          style={{ position: 'fixed', top: 'calc(16px + env(safe-area-inset-top))', right: 16, zIndex: 2 }}
          onClick={openSettings}>
          <IconSettings size={20} color="#64748b" />
        </ActionIcon>
      )}

      <Box style={{ width: '100%', maxWidth: 400, padding: '0 16px', position: 'relative', zIndex: 1 }}>

        {/* Logo / Brand */}
        <Box style={{ textAlign: 'center', marginBottom: 32 }}>
          {tenantInfo?.logoUrl ? (
            // โลโก้ของ Tenant
            <Box style={{ marginBottom: 16 }}>
              <img
                src={tenantInfo.logoUrl}
                alt={tenantInfo.name}
                style={{ maxHeight: 64, maxWidth: 200, objectFit: 'contain', borderRadius: 8 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            </Box>
          ) : (
            // AssetOS default logo
            <Box style={{ marginBottom: 16 }}>
              <img src="/logo-mark.png" alt="AssetOS" style={{ width: 56, height: 56, borderRadius: 14 }} />
            </Box>
          )}

          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 22, fontWeight: 600, color: '#f8fafc', letterSpacing: '-0.4px' }}>
            {tenantInfo?.name
              ? tenantInfo.name
              : <>Asset<span style={{ color: accentColor }}>OS</span></>
            }
          </Text>
          <Text style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
            {tenantInfo ? 'Asset Management Platform' : 'Management Platform'}
          </Text>
        </Box>

        {/* Card */}
        <Box style={{ background: '#1e293b', borderRadius: 16, border: '1px solid rgba(255,255,255,0.06)', padding: '32px 28px' }}>

          {/* Tenant badge removed - company name shown in logo section already */}

          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: '#f1f5f9', marginBottom: 4 }}>
            {t("login.signInToAccount", "Sign in to your account")}
          </Text>
          <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
            {t("login.enterCredentials", "Enter your credentials to continue")}
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={15} />} color="red" mb="md" variant="light" style={{ fontSize: 13 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="on">
            <Stack gap={14}>
              <TextInput
                label={t("common.username","Username")}
                placeholder={t("login.enterUsername","Enter your username")}
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                styles={{
                  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500, marginBottom: 5 },
                  input: { background: '#0f172a', borderColor: 'rgba(255,255,255,0.08)', color: '#f1f5f9', fontSize: 14 },
                }}
              />
              <PasswordInput
                label={t("login.password","Password")}
                placeholder={t("login.enterPassword","Enter your password")}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                styles={{
                  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500, marginBottom: 5 },
                  input: { background: '#0f172a', borderColor: 'rgba(255,255,255,0.08)', color: '#f1f5f9', fontSize: 14 },
                  innerInput: { color: '#f1f5f9' },
                }}
              />
              <Button
                type="submit"
                fullWidth mt={4} loading={loading}
                style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 500, height: 42, fontSize: 14, border: 'none' }}
              >
                {t("login.signIn", "Sign In")}
              </Button>
            </Stack>
          </form>
        </Box>

        <Text style={{ textAlign: 'center', fontSize: 11, color: '#334155', marginTop: 20 }}>
          {t("login.poweredBy", "Powered by")} AssetOS
        </Text>
      </Box>

      {/* ── PIN-protected Backend URL settings (native app เท่านั้น) ── */}
      <Modal opened={settingsOpen} onClose={closeSettingsModal} size="sm" radius="lg" centered
        title={<Text fw={600}>{unlocked ? 'Backend Settings' : 'Enter PIN'}</Text>}>
        {!unlocked ? (
          <Stack align="center" gap="md" py="sm">
            <Text size="sm" c="dimmed" ta="center">{t("login.enterPinHint","Enter PIN to edit backend settings")}</Text>
            <PinInput
              length={6} type="number" mask
              value={pinValue}
              onChange={setPinValue}
              onComplete={handlePinComplete}
              error={pinError}
            />
            {pinError && <Text size="xs" c="red">{t("login.incorrectPin","Incorrect PIN")}</Text>}
          </Stack>
        ) : (
          <Stack gap="md">
            <TextInput
              label={t("login.backendApiUrl","Backend API URL")}
              placeholder="https://assetsservice.com/api"
              value={backendUrlInput}
              onChange={(e) => setBackendUrlInput(e.target.value)}
              description="เซิร์ฟเวอร์เดียวกันสำหรับทุก Tenant — ไม่ต้องเปลี่ยนตามบริษัท ต้องขึ้นต้นด้วย https://"
            />
            <TextInput
              label="Tenant Domain"
              placeholder="ptl.assetsservice.com"
              value={tenantHostInput}
              onChange={(e) => setTenantHostInput(e.target.value)}
              description="โดเมนเฉพาะของบริษัทคุณ (เหมือนที่พิมพ์ในเบราว์เซอร์) — เปลี่ยนตามบริษัทที่ใช้งาน ไม่ต้องมี https:// นำหน้า"
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeSettingsModal}>{t("common.cancel","Cancel")}</Button>
              <Button
                style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}
                onClick={handleSaveBackendUrl}>
                Save &amp; Reload
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  )
}
